/**
 * アクセストークンの取得（仕様書 §8.4）。
 * トークンはメモリ上にのみ保持し、localStorage / sessionStorage には書かない。
 */

export class ReauthRequiredError extends Error {
  constructor() {
    super('再サインインが必要です');
    this.name = 'ReauthRequiredError';
  }
}

export class OfflineError extends Error {
  constructor() {
    super('オフラインのためトークンを取得できません');
    this.name = 'OfflineError';
  }
}

/** 有効期限のこの秒数手前から先回りして更新する。 */
const REFRESH_MARGIN_MS = 120_000;

export class TokenProvider {
  private token: string | null = null;
  private expiresAt = 0;
  private inflight: Promise<string> | null = null;

  async get(): Promise<string> {
    if (this.token && Date.now() < this.expiresAt - REFRESH_MARGIN_MS) return this.token;
    if (this.inflight) return this.inflight;
    this.inflight = this.refresh().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  /** Graph が 401 を返したときに呼び、次回の get() で強制的に取り直させる。 */
  invalidate(): void {
    this.token = null;
    this.expiresAt = 0;
  }

  private async refresh(): Promise<string> {
    let res: Response;
    try {
      res = await fetch('/api/auth/token', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
    } catch {
      throw new OfflineError();
    }

    if (res.status === 401) {
      this.invalidate();
      throw new ReauthRequiredError();
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message ?? `トークンの取得に失敗しました (${res.status})`);
    }

    const { accessToken, expiresAt } = (await res.json()) as {
      accessToken: string;
      expiresAt: string;
    };
    this.token = accessToken;
    this.expiresAt = Date.parse(expiresAt);
    return accessToken;
  }
}

export const tokenProvider = new TokenProvider();

export interface SessionInfo {
  authenticated: boolean;
  account?: { name: string; email: string };
  signedInAt?: string;
  secretExpiresInDays?: number | null;
}

export async function fetchSession(): Promise<SessionInfo> {
  try {
    const res = await fetch('/api/auth/session', { credentials: 'same-origin' });
    if (!res.ok) return { authenticated: false };
    return (await res.json()) as SessionInfo;
  } catch {
    // オフライン時はサインイン状態を確認できない。ローカルデータでの動作は続ける。
    return { authenticated: false };
  }
}

export function startSignIn(returnPath = location.pathname + location.search): void {
  location.href = `/api/auth/login?return=${encodeURIComponent(returnPath)}`;
}

export async function signOut(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
  tokenProvider.invalidate();
}
